/**
 * Canonical family display-name helper (Issue #317, D-13 / r2 memo §R9).
 *
 * Consolidates ≥3 divergent formats duplicated across staff surfaces
 * (ApplicationsQueuePage, ApplicationDetailPage, FamilyProfilePage,
 * FamiliesPage — and CaseloadSchedulePage until its removal on 2026-09-19) into one ratified format:
 *
 *   "{family last name} family · {patient first name}"
 *
 * Fallback chain for the "family last name" slot, per the ratified default:
 *   intake.family_last_name → primary_caregiver.name → patient first name
 *
 * If the chain falls all the way through to the patient's first name, that
 * name is used as the "{X} family" segment itself and is NOT also repeated
 * in the trailing " · {patient first name}" segment (avoids "Jamie family
 * · Jamie").
 *
 * Callers pass whichever fields their data shape has available — omitted
 * fields are simply skipped in the chain.
 */

export interface FamilyDisplayNameInput {
  /** e.g. `application.intake.family_last_name` */
  familyLastName?: string | null;
  /** e.g. `family.primary_caregiver.name` */
  primaryCaregiverName?: string | null;
  /** e.g. `family.patient.first_name` / `event.patient_first_name` */
  patientFirstName?: string | null;
}

export interface FamilyDisplayNameParts {
  /** Resolved "{X}" for the "{X} family" segment, or null if nothing resolved. */
  lastNamePart: string | null;
  /** Resolved trailing patient segment, or null if absent/deduped. */
  patientPart: string | null;
}

/** Rendered when the fallback chain resolves nothing at all. */
export const UNKNOWN_FAMILY_LABEL = 'Unknown family';

function clean(value?: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/**
 * Resolves the two parts of the canonical name independently, for surfaces
 * that need to style or wrap them separately (e.g. an emphasized patient
 * name in a page title) rather than consuming the single joined string.
 */
export function getFamilyDisplayNameParts(input: FamilyDisplayNameInput): FamilyDisplayNameParts {
  const familyLastName = clean(input.familyLastName);
  const primaryCaregiverName = clean(input.primaryCaregiverName);
  const patientFirstName = clean(input.patientFirstName);

  const lastNamePart = familyLastName ?? primaryCaregiverName ?? patientFirstName;
  const usedPatientAsLastName = !familyLastName && !primaryCaregiverName && Boolean(patientFirstName);
  const patientPart = usedPatientAsLastName ? null : patientFirstName;

  return { lastNamePart, patientPart };
}

/**
 * Returns the full canonical display string, e.g. "Alvarez family · Sofia",
 * "Alvarez family" (no patient name available), "Sofia family" (chain fell
 * through to patient first name), or "Unknown family" (nothing resolved).
 */
export function getFamilyDisplayName(input: FamilyDisplayNameInput): string {
  const { lastNamePart, patientPart } = getFamilyDisplayNameParts(input);
  if (!lastNamePart) return UNKNOWN_FAMILY_LABEL;
  return patientPart ? `${lastNamePart} family · ${patientPart}` : `${lastNamePart} family`;
}

// ── The short label (grant lists redesign 2026-09-19, R7) ────────────────────

export interface FamilyShortLabelInput {
  familyLastName?: string | null;
  patientFirstName?: string | null;
  /** The family's sequential number (`F-0142`); the fallback when no name resolves. */
  familyNumber?: number | null;
}

/** Rendered when neither a name nor a number is known. */
export const FAMILY_NAME_MISSING_LABEL = 'Name missing';

/**
 * `Alvarez · Sofia` — the family's last name, then the patient's first name
 * as the second identifier, with no "family" between them (Victor 2026-09-19:
 * "repeating the word family over and over again is annoying"). Falls back to
 * the last name alone, then the patient's name alone, then the F-number, then
 * "Name missing". Never an id.
 */
export function getFamilyShortLabel(input: FamilyShortLabelInput): string {
  const last = clean(input.familyLastName);
  const patient = clean(input.patientFirstName);
  if (last && patient) return `${last} · ${patient}`;
  if (last) return last;
  if (patient) return patient;
  if (typeof input.familyNumber === 'number') return `F-${String(input.familyNumber).padStart(4, '0')}`;
  return FAMILY_NAME_MISSING_LABEL;
}

/**
 * The F-number prints on a row only when two VISIBLE rows share a label
 * (`Smith · Ava · F-0142`); otherwise the label stands alone and the number
 * stays in the fold and on hover. Returns one label per input, in order. A
 * colliding row with no number keeps its bare label — there is nothing
 * honest to append.
 */
export function disambiguateFamilyLabels(
  rows: ReadonlyArray<{ label: string; familyNumber?: number | null }>,
): string[] {
  const seen = new Map<string, Set<number | null>>();
  for (const r of rows) {
    const key = r.label.toLowerCase();
    const set = seen.get(key) ?? new Set<number | null>();
    set.add(typeof r.familyNumber === 'number' ? r.familyNumber : null);
    seen.set(key, set);
  }
  return rows.map((r) => {
    const set = seen.get(r.label.toLowerCase());
    const collides = (set?.size ?? 0) > 1;
    if (!collides || typeof r.familyNumber !== 'number') return r.label;
    return `${r.label} · F-${String(r.familyNumber).padStart(4, '0')}`;
  });
}

/**
 * The two accessible names a staff table row carries for a family (desk pages
 * 2026-09-19, S3): the `<tr>` says who the row is, the link adds the number,
 * and every action on the row is then named by its verb alone — "Call",
 * never "Call the Alvarez family". A screen reader in table mode has already
 * announced the family before it reaches the buttons.
 */
export function familyRowAria(label: string, familyNumber?: number | null): { row: string; link: string } {
  const ref = typeof familyNumber === 'number' ? `, F-${String(familyNumber).padStart(4, '0')}` : '';
  return { row: label, link: `${label}${ref}` };
}
